import { isValid as isValidDateFns, parseISO } from 'date-fns';
import { DateTime, type DateTimeFormatOptions, Duration } from 'luxon';

import { hasValue } from '@core/utils/equality';

import { getDisplayTimezone, getTenantTimezone, getTenantUnits } from '../configuration';
import { DateFilter } from '../types/activity';
import { parseDecimalToNumber } from './decimals';

export const ZERO_DATE_STRING = '0001-01-01T00:00:00Z';
export const ZERO_DURATION_STRING = 'PT0S';

export const DEFAULT_FORECAST_YEAR_FROM = -5;
export const DEFAULT_FORECAST_YEAR_TO = 5;

/**
 * Parse an ISO date string or DateTime object from the server as a date, ignoring
 * any time component and time zone. The resulting date corresponds to midnight
 * local time.
 *
 * @param jsDateStr - ISO date string (e.g., "2025-01-15") or ISO timestamp (time stripped) or DateTime object
 * @returns DateTime object set to midnight local time
 */
export const ParseServerDate = (jsDateStr: string | DateTime = ZERO_DATE_STRING): DateTime => {
  if (typeof jsDateStr === 'string') {
    return DateTime.fromISO(jsDateStr.replace(/T.*$/, '')).setZone('default', {
      keepLocalTime: true,
    });
  }
  return jsDateStr;
};

/**
 * Parse a nullable ISO date string or DateTime object from the server as a date,
 * ignoring any time component and time zone. The resulting date corresponds to
 * midnight local time. If the value is null or undefined, returns null.
 *
 * @param jsDateStr - ISO date string, ISO timestamp, DateTime object, null, or undefined
 * @returns DateTime object set to midnight local time, or null if input is null/undefined
 */
export const ParseServerNullableDate = (
  jsDateStr: string | DateTime | null | undefined,
): DateTime | null =>
  jsDateStr === null || jsDateStr === undefined ? null : ParseServerDate(jsDateStr);

/**
 * Parse a string or DateTime representation of a date from server JSON as a date including
 * the time component and time zone. The resulting date is converted to local time.
 */
export const ParseServerDateTime = (jsDateStr: string | DateTime = ZERO_DATE_STRING): DateTime => {
  if (typeof jsDateStr === 'string') {
    return DateTime.fromISO(jsDateStr);
  }
  return jsDateStr;
};

/**
 * Parse a string or DateTime representation of a date from server JSON as a date including
 * the time component and time zone. The resulting date is converted to local
 * time. If the string is null, returns null.
 */
export const ParseServerNullableDateTime = (
  jsDateStr: string | DateTime | null | undefined,
): DateTime | null =>
  jsDateStr === null || jsDateStr === undefined ? null : ParseServerDateTime(jsDateStr);

/**
 * Parse a string or Duration representation of a duration from server JSON that is in
 * ISO-8601 format.
 */
export const ParseServerDuration = (
  srvDuration: string | Duration = ZERO_DURATION_STRING,
): Duration => {
  if (typeof srvDuration === 'string') {
    return Duration.fromISO(String(srvDuration));
  }
  return srvDuration;
};

/**
 * Parse a string or Duration representation of a duration from server JSON that is in
 * ISO-8601 format. If the string is null, returns null.
 */
export const ParseServerNullableDuration = (
  srvDuration: string | Duration | null | undefined,
): Duration | null =>
  srvDuration === null || srvDuration === undefined ? null : ParseServerDuration(srvDuration);

/**
 * Coalesces a date, returning the default if the date is invalid, null, or undefined.
 */
export const ValidDateOrDefault = (dt: DateTime | null | undefined, def: DateTime): DateTime => {
  if (dt === null || dt === undefined || !dt.isValid) {
    return def;
  }
  return dt;
};

/**
 * Given a date in JS string format, return it in YYYY-MM-DD string format
 */
export const FormatDateString = (jsDateStr: string): string =>
  DateTime.fromISO(jsDateStr).toISODate() ?? '';

/**
 * Given a JS Date from formik (which is typed a Date, but actuallly string),
 * return it in YYYY-MM-DD string format
 */
export const FormatFormikJSDate = (jsDate: Date): string =>
  DateTime.fromISO(`${jsDate}`).toISODate() ?? '';

/**
 * Given a luxon DateTime from formik (which is typed a DateTime, but actually string),
 * return it in YYYY-MM-DD string format
 */
export const FormatFormikDateTime = (luxonDateTime: DateTime): string =>
  DateTime.fromISO(`${luxonDateTime}`).toISODate() ?? '';

/**
 * Given a DateTime, return it in the specified locale string format.
 * Returns empty string if the DateTime is null or invalid.
 *
 * @param luxonDateTime - The DateTime object to format
 * @param options - Formatting options
 * @param options.locale - Locale string (e.g., 'en-US', 'fr-FR')
 * @param options.format - The format options (default: DateTime.DATE_MED)
 */
export const FormatDisplayDateTime = (
  luxonDateTime: DateTime | null | undefined,
  options: { locale: string; format?: DateTimeFormatOptions },
): string => {
  if (!luxonDateTime || !luxonDateTime.isValid) return '';
  const format = options.format ?? DateTime.DATE_MED;
  return luxonDateTime.setLocale(options.locale).toLocaleString(format);
};

/**
 * Given a date in JS string format, return it in the specified locale string format.
 *
 * @param jsDateStr - ISO date string to format
 * @param options - Formatting options
 * @param options.locale - Locale string (e.g., 'en-US', 'fr-FR')
 * @param options.format - The format options (default: DateTime.DATE_MED)
 */
export const FormatDisplayDateString = (
  jsDateStr: string,
  options: { locale: string; format?: DateTimeFormatOptions },
): string => FormatDisplayDateTime(DateTime.fromISO(jsDateStr), options);

/**
 * Like FormatDisplayDateTime, but resolves the locale from the current tenant
 * via `getTenantUnits()` so callers don't have to pass it.
 */
export const FormatTenantDisplayDateTime = (
  luxonDateTime: DateTime | null | undefined,
  format?: DateTimeFormatOptions,
): string => FormatDisplayDateTime(luxonDateTime, { locale: getTenantUnits().locale, format });

export const FormatTenantDisplayTimestamp = (timestamp: DateTime | string | null): string => {
  const dateTime = typeof timestamp === 'string' ? DateTime.fromISO(timestamp) : timestamp;
  return FormatTenantDisplayDateTime(dateTime?.setZone(getTenantTimezone()));
};

/**
 * Like FormatDisplayDateString, but resolves the locale from the current tenant
 * via `getTenantUnits()` so callers don't have to pass it.
 */
export const FormatTenantDisplayDateString = (
  jsDateStr: string,
  format?: DateTimeFormatOptions,
): string => FormatDisplayDateString(jsDateStr, { locale: getTenantUnits().locale, format });

/**
 * Format an ISO date string or DateTime object for display with a specific locale.
 * Parses the ISO string as a date (ignoring time), applies the locale, and formats it.
 * For DateTime objects, converts from UTC to browser's local timezone before formatting as date.
 * Returns empty string if the value is invalid or empty.
 *
 * @param value - ISO date string from server (e.g., "2025-01-15") or Luxon DateTime object
 * @param locale - Locale string (e.g., 'en-US', 'fr-FR')
 * @param format - Luxon DateTimeFormatOptions (default: DateTime.DATE_MED)
 * @returns Formatted date string or empty string if invalid
 *
 * @example
 * FormatIsoDateWithLocale('2025-01-15', 'en-US') // "Jan 15, 2025"
 * FormatIsoDateWithLocale('2025-01-15', 'fr-FR') // "15 janv. 2025"
 * FormatIsoDateWithLocale(luxonDateTime, 'en-US') // "Jan 15, 2025"
 */
export const FormatIsoDateWithLocale = (
  value: unknown,
  locale: string,
  format: DateTimeFormatOptions = DateTime.DATE_MED,
): string => {
  // Handle Luxon DateTime objects - convert to local timezone then format as date
  if (value && typeof value === 'object' && 'isValid' in value && 'setLocale' in value) {
    const dt = value as DateTime;
    return dt.isValid ? dt.setLocale(locale).toLocaleString(format) : '';
  }
  // Handle ISO string
  if (typeof value === 'string' && value) {
    const dt = ParseServerDate(value);
    return dt.isValid ? dt.setLocale(locale).toLocaleString(format) : '';
  }
  return '';
};

/**
 * Format an ISO timestamp string or DateTime object for display with a specific locale.
 * Parses the ISO string as a datetime (including time), applies the locale, and formats it.
 * The timestamp is converted from UTC to the browser's local timezone.
 * Returns empty string if the value is invalid or empty.
 *
 * @param value - ISO timestamp string from server (e.g., "2025-01-15T14:30:00Z") or Luxon DateTime object
 * @param locale - Locale string (e.g., 'en-US', 'fr-FR')
 * @param format - Luxon DateTimeFormatOptions (default: DateTime.DATETIME_MED)
 * @returns Formatted datetime string or empty string if invalid
 *
 * @example
 * FormatIsoDateTimeWithLocale('2025-01-15T14:30:00Z', 'en-US') // "Jan 15, 2025, 2:30 PM"
 * FormatIsoDateTimeWithLocale('2025-01-15T14:30:00Z', 'fr-FR') // "15 janv. 2025, 14:30"
 * FormatIsoDateTimeWithLocale(luxonDateTime, 'en-US', DateTime.DATE_MED) // "Jan 15, 2025"
 */
export const FormatIsoDateTimeWithLocale = (
  value: unknown,
  locale: string,
  format: DateTimeFormatOptions = DateTime.DATETIME_MED,
): string => {
  // Handle Luxon DateTime objects
  if (value && typeof value === 'object' && 'isValid' in value && 'setLocale' in value) {
    const dt = value as DateTime;
    return dt.isValid ? dt.setLocale(locale).toLocaleString(format) : '';
  }
  // Handle ISO string
  if (typeof value === 'string' && value) {
    const dt = ParseServerDateTime(value);
    return dt.isValid ? dt.setLocale(locale).toLocaleString(format) : '';
  }
  return '';
};

/**
 * Given a DateTime, return it in the format required by the value/min/max
 * properties of `<input type="datetime-local">`.
 */
export const FormatDateTimeLocal = (luxonDateTime: DateTime): string =>
  luxonDateTime.startOf('minute').toISO({
    includeOffset: false,
    suppressSeconds: true,
    suppressMilliseconds: true,
  }) ?? '';

/**
 * Like `FormatDateTimeLocal` but keeps sub-minute precision: the bare wall digits of a Formik
 * datetime value, so an unedited field round-trips its original seconds instead of truncating.
 */
export const FormatWallDateTime = (luxonDateTime: DateTime): string =>
  luxonDateTime.toISO({ includeOffset: false }) ?? '';

/**
 * Given a string (ISO format) or Date, returns a valid Date object or null.
 * Returns null if the input is null, empty, or not a valid date.
 */
export const ParseInputValueToDate = (value: string | Date | null): Date | null => {
  if (!hasValue(value) || value === '') return null;
  if (value instanceof Date && isValidDateFns(value)) return value;
  if (typeof value === 'string') {
    const date = parseISO(value);
    return isValidDateFns(date) ? date : null;
  }
  return null;
};

/**
 * Given an unadjusted duration and a ratio, increase the duration by the
 * specified adjustment ratio (provided as a valid, non-empty decimal string).
 *
 * Examples:
 * - duration 1 hour, adjustment ratio 0.5  => adjusted duration  90 minutes
 * - duration 1 hour, adjustment ratio -0.5 => adjusted duration  30 minutes
 * - duration 1 hour, adjustment ratio 1.5  => adjusted duration 150 minutes
 */
export const adjustDuration = (unadjusted: Duration, adjustmentRatio: string = '0'): Duration => {
  const drivingTimeFactor = 1 + parseDecimalToNumber(adjustmentRatio);
  return Duration.fromObject({
    minutes: unadjusted.as('minutes') * drivingTimeFactor,
  });
};

/**
 * Round a duration to the nearest whole minute.
 *
 * Drive times are persisted in `INTERVAL HOUR TO MINUTE` columns, which discard
 * seconds, so rounding here keeps the previewed, sent, and stored values in sync.
 */
export const roundDurationToMinutes = (duration: Duration): Duration =>
  Duration.fromObject({ minutes: Math.round(duration.as('minutes')) });

export const apiDateFormat = (date: DateTime): string => date.toFormat('yyyy-MM-dd HH:mm:ss');

export const apiUtcFormat = (date: DateTime): string => apiDateFormat(date.toUTC());

// Reads a picked calendar date as wall time in the display zone.
export const apiUtcFormatInDisplayZone = (date: DateTime): string =>
  apiUtcFormat(date.setZone(getDisplayTimezone(), { keepLocalTime: true }));

export const displayNow = (): DateTime => DateTime.now().setZone(getDisplayTimezone());

export const toDisplayZone = (date: DateTime | string): DateTime =>
  ParseServerDateTime(date).setZone(getDisplayTimezone());

// Formats a stored time on the display zone's clock, in the tenant's locale.
export const FormatDisplayZoneDateTime = (
  value: DateTime | string,
  format?: DateTimeFormatOptions,
): string => FormatTenantDisplayDateTime(toDisplayZone(value), format);

// Formats a stored time as time only (e.g. "2:30 PM") on the display zone's clock.
export const FormatTimeWithLocale = (
  value: DateTime | string,
  locale: string,
  format: string = 'h:mm a',
): string => {
  const dateTime = toDisplayZone(value);
  return dateTime.isValid ? dateTime.setLocale(locale).toFormat(format) : '';
};

// Form fields show display-zone wall time; a picker may tag the picked digits with the device offset.
export const readAsDisplayWallTime = (value: string): DateTime =>
  DateTime.fromISO(value, { zone: getDisplayTimezone(), setZone: true }).setZone(
    getDisplayTimezone(),
    {
      keepLocalTime: true,
    },
  );

export const isDisplayToday = (date: DateTime | string): boolean =>
  toDisplayZone(date).hasSame(displayNow(), 'day');

export const getDateRangeFilter = (
  dateFilter: DateFilter,
): { startTime: string; endTime: string } | null => {
  const now = displayNow();

  switch (dateFilter) {
    case DateFilter.Today:
      return {
        startTime: apiUtcFormat(now.startOf('day')),
        endTime: apiUtcFormat(now.endOf('day')),
      };
    case DateFilter.Yesterday:
      return {
        startTime: apiUtcFormat(now.minus({ days: 1 }).startOf('day')),
        endTime: apiUtcFormat(now.minus({ days: 1 }).endOf('day')),
      };
    case DateFilter.Last7Days:
      return {
        startTime: apiUtcFormat(now.minus({ days: 7 }).startOf('day')),
        endTime: apiUtcFormat(now.endOf('day')),
      };
    case DateFilter.Last14Days:
      return {
        startTime: apiUtcFormat(now.minus({ days: 14 }).startOf('day')),
        endTime: apiUtcFormat(now.endOf('day')),
      };
    case DateFilter.Last30Days:
      return {
        startTime: apiUtcFormat(now.minus({ days: 30 }).startOf('day')),
        endTime: apiUtcFormat(now.endOf('day')),
      };
    case DateFilter.ThisWeek:
      return {
        startTime: apiUtcFormat(now.startOf('week')),
        endTime: apiUtcFormat(now.endOf('week')),
      };
    case DateFilter.ThisMonth:
      return {
        startTime: apiUtcFormat(now.startOf('month')),
        endTime: apiUtcFormat(now.endOf('month')),
      };
    case DateFilter.LastMonth:
      return {
        startTime: apiUtcFormat(now.minus({ months: 1 }).startOf('month')),
        endTime: apiUtcFormat(now.minus({ months: 1 }).endOf('month')),
      };
    case DateFilter.AllTime:
    default:
      return null;
  }
};
